/**
 * Presentation metadata for every domain enum: the human label and the badge tone.
 *
 * The UI must read labels from here rather than hardcoding strings, so that
 * "SKIPPED_PRIOR_KNOWLEDGE" renders identically in the lesson header, the intern
 * detail page, the skip report and the CSV export.
 */
/* -------------------------------------------------------------------------- */
export const ROLE_DISPLAY = {
    ADMIN: { label: 'Admin', tone: 'accent', description: 'Full platform access.' },
    MENTOR: { label: 'Mentor', tone: 'info', description: 'Teaches, reviews and manages interns.' },
    INTERN: { label: 'Intern', tone: 'neutral', description: 'Follows the learning path.' },
};
export const INTERN_STATUS_DISPLAY = {
    ACTIVE: { label: 'Active', tone: 'success' },
    ON_HOLD: { label: 'On hold', tone: 'warning' },
    COMPLETED: { label: 'Completed', tone: 'info' },
    GRADUATED: { label: 'Graduated', tone: 'accent' },
    INACTIVE: { label: 'Inactive', tone: 'neutral' },
};
export const PROJECT_READINESS_DISPLAY = {
    NOT_READY: {
        label: 'Not ready',
        tone: 'neutral',
        description: 'Outstanding learning requirements remain.',
    },
    READY: {
        label: 'Ready',
        tone: 'success',
        description: 'All requirements met; can be assigned to a project.',
    },
    IN_PROJECT: { label: 'In project', tone: 'info', description: 'Working on a real Aforium project.' },
    GRADUATED: { label: 'Graduated', tone: 'accent', description: 'Completed the programme.' },
};
export const SKILL_LEVEL_DISPLAY = {
    BEGINNER: { label: 'Beginner', tone: 'neutral' },
    INTERMEDIATE: { label: 'Intermediate', tone: 'info' },
    ADVANCED: { label: 'Advanced', tone: 'success' },
    EXPERT: { label: 'Expert', tone: 'accent' },
};
export const COURSE_DIFFICULTY_DISPLAY = {
    BEGINNER: { label: 'Beginner', tone: 'success' },
    INTERMEDIATE: { label: 'Intermediate', tone: 'info' },
    ADVANCED: { label: 'Advanced', tone: 'warning' },
};
export const LESSON_STATUS_DISPLAY = {
    NOT_STARTED: { label: 'Not started', tone: 'neutral' },
    IN_PROGRESS: { label: 'In progress', tone: 'info' },
    COMPLETED: { label: 'Completed', tone: 'success' },
    SKIPPED: { label: 'Skipped', tone: 'accent' },
    FAILED: { label: 'Failed', tone: 'danger' },
};
export const LESSON_RESOURCE_TYPE_DISPLAY = {
    LINK: { label: 'Link', tone: 'neutral' },
    ARTICLE: { label: 'Article', tone: 'info' },
    VIDEO: { label: 'Video', tone: 'danger' },
    DOCUMENTATION: { label: 'Docs', tone: 'accent' },
    REPOSITORY: { label: 'Code', tone: 'neutral' },
    DOWNLOAD: { label: 'Download', tone: 'success' },
};
export const COMPLETION_METHOD_DISPLAY = {
    NORMAL: {
        label: 'Completed normally',
        tone: 'success',
        description: 'Studied the lesson and met its requirements.',
    },
    SKIPPED_PRIOR_KNOWLEDGE: {
        label: 'Skipped — prior knowledge',
        tone: 'accent',
        description: 'Passed a skip assessment demonstrating existing knowledge.',
    },
    ADMIN_OVERRIDE: {
        label: 'Admin override',
        tone: 'warning',
        description: 'A mentor or admin marked this requirement satisfied.',
    },
    RECAP: {
        label: 'Completed via Recap',
        tone: 'info',
        description: 'Covered by the course recap: approved recap exercise and a passed recap quiz.',
    },
};
export const COURSE_PROGRESS_STATUS_DISPLAY = {
    NOT_STARTED: { label: 'Not started', tone: 'neutral' },
    IN_PROGRESS: { label: 'In progress', tone: 'info' },
    COMPLETED: { label: 'Completed', tone: 'success' },
};
export const EXERCISE_TYPE_DISPLAY = {
    QUIZ: { label: 'Quiz', tone: 'info' },
    CODE: { label: 'Code', tone: 'accent' },
    DEBUGGING: { label: 'Debugging', tone: 'warning' },
    ARCHITECTURE: { label: 'Architecture', tone: 'accent' },
    DATABASE: { label: 'Database', tone: 'info' },
    API: { label: 'API', tone: 'info' },
    UI: { label: 'UI', tone: 'accent' },
    SHORT_ANSWER: { label: 'Short answer', tone: 'neutral' },
    PROJECT_TASK: { label: 'Project task', tone: 'success' },
};
export const EXERCISE_RUNTIME_DISPLAY = {
    NONE: { label: 'Review only', tone: 'neutral' },
    JAVASCRIPT: { label: 'JavaScript', tone: 'warning' },
    TYPESCRIPT: { label: 'TypeScript', tone: 'info' },
    HTML: { label: 'HTML & CSS', tone: 'accent' },
    REACT: { label: 'React', tone: 'info' },
};
export const EXERCISE_DIFFICULTY_DISPLAY = {
    EASY: { label: 'Easy', tone: 'success' },
    MEDIUM: { label: 'Medium', tone: 'info' },
    HARD: { label: 'Hard', tone: 'warning' },
};
export const SUBMISSION_STATUS_DISPLAY = {
    DRAFT: { label: 'Draft', tone: 'neutral' },
    SUBMITTED: { label: 'Submitted', tone: 'info' },
    UNDER_REVIEW: { label: 'Under review', tone: 'warning' },
    APPROVED: { label: 'Approved', tone: 'success' },
    NEEDS_CHANGES: { label: 'Needs changes', tone: 'warning' },
    REJECTED: { label: 'Rejected', tone: 'danger' },
};
export const FEEDBACK_CATEGORY_DISPLAY = {
    GENERAL: { label: 'General', tone: 'neutral' },
    CODE_QUALITY: { label: 'Code quality', tone: 'info' },
    LOGIC: { label: 'Logic', tone: 'info' },
    ARCHITECTURE: { label: 'Architecture', tone: 'accent' },
    UI: { label: 'UI', tone: 'accent' },
    BACKEND: { label: 'Backend', tone: 'info' },
    TESTING: { label: 'Testing', tone: 'success' },
    PROBLEM_SOLVING: { label: 'Problem solving', tone: 'warning' },
};
export const QUESTION_TYPE_DISPLAY = {
    MULTIPLE_CHOICE: { label: 'Multiple choice', tone: 'info' },
    MULTI_SELECT: { label: 'Multi-select', tone: 'info' },
    TRUE_FALSE: { label: 'True / false', tone: 'neutral' },
    SHORT_ANSWER: { label: 'Short answer', tone: 'accent' },
};
export const ATTEMPT_STATUS_DISPLAY = {
    IN_PROGRESS: { label: 'In progress', tone: 'info' },
    PASSED: { label: 'Passed', tone: 'success' },
    FAILED: { label: 'Failed', tone: 'danger' },
    EXPIRED: { label: 'Expired', tone: 'warning' },
    ABANDONED: { label: 'Abandoned', tone: 'neutral' },
};
export const ASSESSMENT_TYPE_DISPLAY = {
    FINAL: { label: 'Final assessment', tone: 'accent' },
    COURSE: { label: 'Course assessment', tone: 'info' },
    PLACEMENT: { label: 'Placement assessment', tone: 'neutral' },
    RECAP: { label: 'Recap quiz', tone: 'info' },
};
export const SKIP_REQUEST_STATUS_DISPLAY = {
    PENDING: { label: 'Pending', tone: 'neutral' },
    ASSESSMENT_IN_PROGRESS: { label: 'Assessment in progress', tone: 'info' },
    APPROVED: { label: 'Approved', tone: 'success' },
    REJECTED: { label: 'Rejected', tone: 'danger' },
    CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};
export const ACHIEVEMENT_TIER_DISPLAY = {
    BRONZE: { label: 'Bronze', tone: 'warning' },
    SILVER: { label: 'Silver', tone: 'neutral' },
    GOLD: { label: 'Gold', tone: 'accent' },
    PLATINUM: { label: 'Platinum', tone: 'info' },
};
export const CERTIFICATE_STATUS_DISPLAY = {
    ISSUED: { label: 'Issued', tone: 'success' },
    REVOKED: { label: 'Revoked', tone: 'danger' },
};
export const PROJECT_STATUS_DISPLAY = {
    PLANNING: { label: 'Planning', tone: 'neutral' },
    IN_PROGRESS: { label: 'In progress', tone: 'info' },
    ON_HOLD: { label: 'On hold', tone: 'warning' },
    COMPLETED: { label: 'Completed', tone: 'success' },
    CANCELLED: { label: 'Cancelled', tone: 'danger' },
};
export const TASK_STATUS_DISPLAY = {
    TODO: { label: 'To do', tone: 'neutral' },
    IN_PROGRESS: { label: 'In progress', tone: 'info' },
    IN_REVIEW: { label: 'In review', tone: 'warning' },
    CHANGES_REQUESTED: { label: 'Changes requested', tone: 'warning' },
    APPROVED: { label: 'Approved', tone: 'success' },
    DONE: { label: 'Done', tone: 'success' },
    BLOCKED: { label: 'Blocked', tone: 'danger' },
};
export const TASK_PRIORITY_DISPLAY = {
    LOW: { label: 'Low', tone: 'neutral' },
    MEDIUM: { label: 'Medium', tone: 'info' },
    HIGH: { label: 'High', tone: 'warning' },
    URGENT: { label: 'Urgent', tone: 'danger' },
};
export const PROJECT_MEMBER_ROLE_DISPLAY = {
    LEAD: { label: 'Lead', tone: 'accent' },
    DEVELOPER: { label: 'Developer', tone: 'info' },
    REVIEWER: { label: 'Reviewer', tone: 'success' },
    OBSERVER: { label: 'Observer', tone: 'neutral' },
};
export const CLIENT_STATUS_DISPLAY = {
    PROSPECT: { label: 'Prospect', tone: 'info' },
    ACTIVE: { label: 'Active', tone: 'success' },
    INACTIVE: { label: 'Inactive', tone: 'neutral' },
    ARCHIVED: { label: 'Archived', tone: 'neutral' },
};
export const EXPENSE_CATEGORY_DISPLAY = {
    SOFTWARE: { label: 'Software', tone: 'info' },
    HOSTING: { label: 'Hosting', tone: 'info' },
    DOMAIN: { label: 'Domain', tone: 'neutral' },
    MARKETING: { label: 'Marketing', tone: 'accent' },
    EMPLOYEE_COST: { label: 'Employee cost', tone: 'warning' },
    EQUIPMENT: { label: 'Equipment', tone: 'neutral' },
    OTHER: { label: 'Other', tone: 'neutral' },
};
export const REVENUE_PAYMENT_STATUS_DISPLAY = {
    PENDING: { label: 'Pending', tone: 'neutral' },
    PARTIALLY_PAID: { label: 'Partially paid', tone: 'warning' },
    PAID: { label: 'Paid', tone: 'success' },
    OVERDUE: { label: 'Overdue', tone: 'danger' },
    CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};
export const INVOICE_STATUS_DISPLAY = {
    DRAFT: { label: 'Draft', tone: 'neutral' },
    SENT: { label: 'Sent', tone: 'info' },
    PARTIALLY_PAID: { label: 'Partially paid', tone: 'warning' },
    PAID: { label: 'Paid', tone: 'success' },
    OVERDUE: { label: 'Overdue', tone: 'danger' },
    CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};
export const PAYMENT_METHOD_DISPLAY = {
    BANK_TRANSFER: { label: 'Bank transfer', tone: 'info' },
    CARD: { label: 'Card', tone: 'info' },
    CASH: { label: 'Cash', tone: 'neutral' },
    PAYPAL: { label: 'PayPal', tone: 'info' },
    STRIPE: { label: 'Stripe', tone: 'accent' },
    OTHER: { label: 'Other', tone: 'neutral' },
};
/* -------------------------------------------------------------------------- */
/* Platform defaults                                                          */
/* -------------------------------------------------------------------------- */
/** Defaults applied when an admin does not configure a value explicitly. */
export const DEFAULTS = {
    /** Passing score for lesson quizzes, as a percentage. */
    QUIZ_PASSING_SCORE: 80,
    /** Passing score for a skip assessment (spec section 19). */
    SKIP_ASSESSMENT_PASSING_SCORE: 80,
    /** Passing score for the final assessment (spec section 46). */
    FINAL_ASSESSMENT_PASSING_SCORE: 80,
    /** Attempt allowance for a skip assessment. */
    SKIP_ASSESSMENT_MAX_ATTEMPTS: 3,
    /** Attempt allowance for a lesson quiz. */
    /**
     * A knowledge check is there to find out whether the lesson landed, not to
     * ration the finding out. Three attempts meant one intern sat locked out of
     * a required lesson — and every lesson behind it — for five days over a quiz
     * he scored 60% on. Ten leaves room to go back and read.
     */
    QUIZ_MAX_ATTEMPTS: 10,
    /** Question count drawn for a skip assessment. */
    SKIP_ASSESSMENT_QUESTION_COUNT: 10,
    /** Points awarded per question when a question does not override it. */
    QUESTION_POINTS: 1,
    /** Currency used across the business management module. */
    CURRENCY: 'USD',
    /** Default invoice payment terms, in days. */
    INVOICE_DUE_DAYS: 14,
    /** Hours a submitted exercise may wait for a mentor before it is escalated. */
    REVIEW_SLA_HOURS: 48,
    /** Organisation name used on certificates and in the shell. */
    ORGANISATION_NAME: 'Aforium',
    /**
     * Appended to the organisation name to build the default certificate program
     * name, so `Aforium` yields the `Certificate.programName` schema default.
     */
    CERTIFICATE_PROGRAM_SUFFIX: 'Learning — Developer Training',
};
/**
 * Keys of the admin-editable rows in `PlatformSetting`.
 *
 * The seed writes these keys, the settings page edits them and the services
 * read them through `getSetting`, so they live here rather than as loose
 * strings in three places — a typo would otherwise silently fall back to the
 * compiled-in default forever.
 */
export const SETTING_KEYS = {
    /** Default pass mark for a new FINAL assessment. */
    FINAL_ASSESSMENT_PASSING_SCORE: 'final_assessment.passing_score',
    /** Default pass mark for a new skip assessment. */
    SKIP_ASSESSMENT_PASSING_SCORE: 'skip_assessment.default_passing_score',
    /** Default attempt allowance for a new skip assessment. */
    SKIP_ASSESSMENT_MAX_ATTEMPTS: 'skip_assessment.default_max_attempts',
    /** Review turnaround target, in hours. */
    REVIEW_SLA_HOURS: 'review.sla_hours',
    /** Organisation name printed on certificates. */
    ORGANISATION_NAME: 'organisation.name',
};
/** Short human label for a numeric percentage, e.g. `92%`. */
export function formatPercent(value, fractionDigits = 0) {
    if (value === null || value === undefined || Number.isNaN(value))
        return '—';
    return `${value.toFixed(fractionDigits)}%`;
}
/** Money formatter shared by the finance module and the accountant dashboard. */
export function formatCurrency(amount, currency = DEFAULTS.CURRENCY) {
    if (amount === null || amount === undefined || Number.isNaN(amount))
        return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
    }).format(amount);
}
/** `95` minutes becomes `1h 35m`; `40` becomes `40m`. */
export function formatDuration(minutes) {
    if (minutes === null || minutes === undefined || minutes <= 0)
        return '—';
    const hours = Math.floor(minutes / 60);
    const remainder = Math.round(minutes % 60);
    if (hours === 0)
        return `${remainder}m`;
    if (remainder === 0)
        return `${hours}h`;
    return `${hours}h ${remainder}m`;
}
//# sourceMappingURL=constants.js.map